import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Box,
  CircularProgress,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  TextField,
  Grid,
  Button,
  Alert,
  Autocomplete,
} from "@mui/material";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { getBandejaNormativas } from "../../componentes/http/actions/BandejaNormativaAction";
import BandejaDataLoader from "../../componentes/data/BandejaDataLoader";
import RegistrarDesdeScraperModal from "../../componentes/modal/Bandeja/RegistrarDesdeScraperModal";

const BandejaNormativas = () => {
  const [normativas, setNormativas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedRows, setExpandedRows] = useState({});
  const [error, setError] = useState(null);
  const [paisActivo, setPaisActivo] = useState(null);

  const [modalRegistrar, setModalRegistrar] = useState({
    open: false,
    normativa: null,
  });

  const [catalogos, setCatalogos] = useState({
    reguladores: [],
    tiposDocumento: [],
    rubros: [],
    paises: []
  });

  const [filtros, setFiltros] = useState({
    nombre: '',
    regulador: null,
    tipoDocumento: null,
    rubro: null,
    pais: null,
    fechaDesde: '',
    fechaHasta: ''
  });

  const [filtrosTemp, setFiltrosTemp] = useState({
    nombre: '',
    regulador: null,
    tipoDocumento: null,
    rubro: null,
    pais: null,
    fechaDesde: '',
    fechaHasta: ''
  });

  const handleDataLoaded = useCallback((data) => {
    setCatalogos(data);
    const chile = data.paises.find(p => p.nombre.toLowerCase() === 'chile');
    if (chile) setPaisActivo(chile.id);
  }, []);

  const reguladoresFiltrados = catalogos.reguladores.filter(
    r => r.paisId === paisActivo
  );

  const fetchNormativas = useCallback(() => {
    setIsLoading(true);
    setError(null);
    const token = localStorage.getItem("token");

    const filtrosBackend = {
      nombre: filtros.nombre || undefined,
      regulador: filtros.regulador?.nombre || undefined,
      tipoDocumento: filtros.tipoDocumento?.nombre || undefined,
      rubro: filtros.rubro?.nombre || undefined,
      pais: catalogos.paises.find(p => p.id === paisActivo)?.nombre || undefined,
      fechaDesde: filtros.fechaDesde || undefined,
      fechaHasta: filtros.fechaHasta || undefined,
    };

    getBandejaNormativas(page + 1, rowsPerPage, filtrosBackend, token)
      .then((response) => {
        if (!response || !response.success) {
          setNormativas([]);
          setTotalCount(0);
          return;
        }
        setNormativas(response.items || []);
        setTotalCount(response.totalCount || 0);
      })
      .catch((error) => {
        console.error("Error al cargar bandeja:", error);
        setError("Error al cargar las normativas. Por favor, intente nuevamente.");
        setNormativas([]);
        setTotalCount(0);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filtros.nombre, filtros.regulador?.nombre, filtros.tipoDocumento?.nombre, filtros.rubro?.nombre, filtros.fechaDesde, filtros.fechaHasta, catalogos.paises, page, rowsPerPage, paisActivo]);

  useEffect(() => {
    fetchNormativas();
  }, [fetchNormativas]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setExpandedRows({});
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setExpandedRows({});
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltrosTemp(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const aplicarFiltros = () => {
    setFiltros(filtrosTemp);
    setPage(0);
    setExpandedRows({});
  };

  const limpiarFiltros = () => {
    const filtrosVacios = {
      nombre: '',
      regulador: null,
      tipoDocumento: null,
      rubro: null,
      pais: null,
      fechaDesde: '',
      fechaHasta: ''
    };
    setFiltrosTemp(filtrosVacios);
    setFiltros(filtrosVacios);
    setPage(0);
    setExpandedRows({});
  };

  const hayFiltrosActivos = filtros.nombre || filtros.regulador || filtros.tipoDocumento || filtros.rubro || filtros.pais || filtros.fechaDesde || filtros.fechaHasta;

  const formatearFecha = (fecha) => {
    if (!fecha) return 'Sin fecha';
    const fechaObj = new Date(fecha);
    if (isNaN(fechaObj.getTime())) return 'Fecha no válida';
    const year = fechaObj.getFullYear();
    if (year < 1900 || year > 2100) return 'Sin fecha';
    if (year === 1 || (year === 1970 && fechaObj.getMonth() === 0 && fechaObj.getDate() === 1)) return 'Sin fecha';
    return fechaObj.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const FechaCell = ({ fecha }) => {
    const fechaFormateada = formatearFecha(fecha);
    const esFechaInvalida = fechaFormateada === 'Sin fecha' || fechaFormateada === 'Fecha no válida';
    return (
      <Tooltip title={esFechaInvalida ? "La fecha no está disponible o es inválida" : ""}>
        <Typography
          variant="body2"
          sx={{
            color: esFechaInvalida ? 'text.disabled' : 'text.primary',
            fontStyle: esFechaInvalida ? 'italic' : 'normal',
            fontSize: '0.875rem'
          }}
        >
          {fechaFormateada}
        </Typography>
      </Tooltip>
    );
  };

  const handleAbrirModalRegistrar = (normativa) => {
    setModalRegistrar({ open: true, normativa });
  };

  const handleCerrarModalRegistrar = () => {
    setModalRegistrar({ open: false, normativa: null });
  };

  const handleRegistroExitoso = () => {
    fetchNormativas();
  };

  if (isLoading && normativas.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <BandejaDataLoader onDataLoaded={handleDataLoaded} />

      <Typography variant="h5" sx={{ mb: 2 }}>
        Bandeja de Normativas
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={paisActivo}
          onChange={(e, newValue) => {
            setPaisActivo(newValue);
            setFiltrosTemp(prev => ({ ...prev, regulador: null }));
            setFiltros(prev => ({ ...prev, regulador: null, pais: catalogos.paises.find(p => p.id === newValue)?.nombre }));
            setPage(0);
          }}
        >
          {catalogos.paises.map(pais => (
            <Tab key={pais.id} label={pais.nombre} value={pais.id} />
          ))}
        </Tabs>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Filtros de Búsqueda
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Nombre"
              variant="outlined"
              size="small"
              value={filtrosTemp.nombre}
              onChange={(e) => handleFiltroChange('nombre', e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') aplicarFiltros(); }}
              placeholder="Buscar por nombre..."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={reguladoresFiltrados}
              getOptionLabel={(option) => option.nombre || ""}
              renderInput={(params) => (
                <TextField {...params} label="Regulador" size="small" placeholder="Seleccionar regulador..." />
              )}
              value={filtrosTemp.regulador}
              onChange={(event, newValue) => handleFiltroChange('regulador', newValue)}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={catalogos.tiposDocumento}
              getOptionLabel={(option) => option.nombre || ""}
              renderInput={(params) => (
                <TextField {...params} label="Tipo Documento" size="small" placeholder="Seleccionar tipo..." />
              )}
              value={filtrosTemp.tipoDocumento}
              onChange={(event, newValue) => handleFiltroChange('tipoDocumento', newValue)}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={catalogos.rubros}
              getOptionLabel={(option) => option.nombre || ""}
              renderInput={(params) => (
                <TextField {...params} label="Rubro" size="small" placeholder="Seleccionar rubro..." />
              )}
              value={filtrosTemp.rubro}
              onChange={(event, newValue) => handleFiltroChange('rubro', newValue)}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Fecha desde"
              type="date"
              variant="outlined"
              size="small"
              value={filtrosTemp.fechaDesde}
              onChange={(e) => handleFiltroChange('fechaDesde', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Fecha hasta"
              type="date"
              variant="outlined"
              size="small"
              value={filtrosTemp.fechaHasta}
              onChange={(e) => handleFiltroChange('fechaHasta', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: filtrosTemp.fechaDesde || undefined }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', gap: 1, height: '100%' }}>
              <Button fullWidth variant="contained" startIcon={<SearchIcon />} onClick={aplicarFiltros} sx={{ height: '40px' }}>
                Buscar
              </Button>
              <Button fullWidth variant="outlined" startIcon={<ClearIcon />} onClick={limpiarFiltros} disabled={!hayFiltrosActivos} sx={{ height: '40px' }}>
                Limpiar
              </Button>
            </Box>
          </Grid>
        </Grid>

        {hayFiltrosActivos && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Filtros activos:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {filtros.nombre && (
                <Chip label={`Nombre: ${filtros.nombre}`} size="small" color="primary"
                  onDelete={() => { const f = { ...filtrosTemp, nombre: '' }; setFiltrosTemp(f); setFiltros(f); setPage(0); }}
                />
              )}
              {filtros.regulador && (
                <Chip label={`Regulador: ${filtros.regulador.nombre}`} size="small" color="primary"
                  onDelete={() => { const f = { ...filtrosTemp, regulador: null }; setFiltrosTemp(f); setFiltros(f); setPage(0); }}
                />
              )}
              {filtros.tipoDocumento && (
                <Chip label={`Tipo: ${filtros.tipoDocumento.nombre}`} size="small" color="primary"
                  onDelete={() => { const f = { ...filtrosTemp, tipoDocumento: null }; setFiltrosTemp(f); setFiltros(f); setPage(0); }}
                />
              )}
              {filtros.rubro && (
                <Chip label={`Rubro: ${filtros.rubro.nombre}`} size="small" color="primary"
                  onDelete={() => { const f = { ...filtrosTemp, rubro: null }; setFiltrosTemp(f); setFiltros(f); setPage(0); }}
                />
              )}
              {filtros.fechaDesde && (
                <Chip label={`Desde: ${filtros.fechaDesde}`} size="small" color="primary"
                  onDelete={() => { const f = { ...filtrosTemp, fechaDesde: '' }; setFiltrosTemp(f); setFiltros(f); setPage(0); }}
                />
              )}
              {filtros.fechaHasta && (
                <Chip label={`Hasta: ${filtros.fechaHasta}`} size="small" color="primary"
                  onDelete={() => { const f = { ...filtrosTemp, fechaHasta: '' }; setFiltrosTemp(f); setFiltros(f); setPage(0); }}
                />
              )}
            </Box>
          </Box>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {isLoading ? 'Cargando...' : `${totalCount} resultado(s) encontrado(s)`}
        </Typography>
        {isLoading && normativas.length > 0 && <CircularProgress size={20} />}
      </Box>

      <TableContainer component={Paper}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50, fontWeight: 'bold' }}></TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Regulador</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Tipo Documento</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 100 }}>País</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 80 }} align="center">Rubros</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 100 }} align="center">Compromisos</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 100 }} align="center">Obligaciones</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 110 }} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {normativas.map((normativa) => (
              <React.Fragment key={normativa.id}>
                <TableRow
                    hover
                    sx={normativa.yaRegistrada ? { backgroundColor: 'action.selected' } : undefined}
                  >
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => toggleRowExpansion(normativa.id)}
                      aria-label={expandedRows[normativa.id] ? "Colapsar" : "Expandir"}
                    >
                      {expandedRows[normativa.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300, wordBreak: 'break-word' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2">{normativa.nombre || 'Sin nombre'}</Typography>
                      {normativa.yaRegistrada && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Registrada"
                          size="small"
                          color="success"
                          variant="filled"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{normativa.regulador || 'N/A'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{normativa.tipoDocumento || 'N/A'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{normativa.pais || 'N/A'}</Typography>
                  </TableCell>
                  <TableCell>
                    <FechaCell fecha={normativa.fecha} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={normativa.rubros?.length || 0} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={normativa.compromisos?.length || 0} size="small" color="secondary" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={normativa.obligaciones?.length || 0} size="small" color="info" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={normativa.yaRegistrada ? "Ya registrada en Alerta Normativa" : "Registrar en Alerta Normativa"}>
                      <span>
                        <IconButton
                          size="small"
                          color={normativa.yaRegistrada ? "success" : "primary"}
                          onClick={() => handleAbrirModalRegistrar(normativa)}
                          disabled={normativa.yaRegistrada}
                        >
                          {normativa.yaRegistrada
                            ? <CheckCircleIcon fontSize="small" />
                            : <AddCircleOutlineIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>

                    {normativa.linkSitioWeb && (
                      <Tooltip title="Ver documento original">
                        <IconButton
                          size="small"
                          color="primary"
                          component="a"
                          href={normativa.linkSitioWeb}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                    <Collapse in={expandedRows[normativa.id]} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 2 }}>
                        <Typography variant="h6" gutterBottom component="div">Detalles</Typography>

                        {normativa.descripcion && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold">Descripción:</Typography>
                            <Typography variant="body2" color="text.secondary">{normativa.descripcion}</Typography>
                          </Box>
                        )}

                        {normativa.rubros?.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                              Rubros ({normativa.rubros.length}):
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {normativa.rubros.map((rubro, idx) => (
                                <Chip key={idx} label={rubro.nombre} size="small" variant="outlined" color="primary" />
                              ))}
                            </Box>
                          </Box>
                        )}

                        {normativa.compromisos?.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                              Compromisos ({normativa.compromisos.length}):
                            </Typography>
                            {normativa.compromisos.map((c, idx) => (
                              <Box key={idx} sx={{ mb: 1, pl: 2 }}>
                                <Typography variant="body2" color="text.secondary">• {c.descripcion}</Typography>
                              </Box>
                            ))}
                          </Box>
                        )}

                        {normativa.obligaciones?.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                              Obligaciones ({normativa.obligaciones.length}):
                            </Typography>
                            {normativa.obligaciones.map((o, idx) => (
                              <Box key={idx} sx={{ mb: 1, pl: 2 }}>
                                <Typography variant="body2" color="text.secondary">• {o.descripcion}</Typography>
                              </Box>
                            ))}
                          </Box>
                        )}

                        {normativa.documentos?.length > 0 && (
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Documentos:</Typography>
                            {normativa.documentos.map((doc, idx) => (
                              <Box key={idx} sx={{ mb: 1, pl: 2 }}>
                                {doc.urlDocumento && (
                                  <Typography
                                    variant="body2" component="a"
                                    href={doc.urlDocumento} target="_blank" rel="noopener noreferrer"
                                    sx={{ color: 'primary.main', textDecoration: 'none' }}
                                  >
                                    • Ver documento {idx + 1}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}

            {normativas.length === 0 && !isLoading && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No se encontraron resultados con los filtros aplicados
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />

      <RegistrarDesdeScraperModal
        open={modalRegistrar.open}
        onClose={handleCerrarModalRegistrar}
        normativaScraper={modalRegistrar.normativa}
        onSuccess={handleRegistroExitoso}
      />
    </Box>
  );
};

export default BandejaNormativas;